// @flow
import { AdSlot, Query, SeoData, SortableTable, } from '@haaretz/htz-components';
import React, { Fragment, } from 'react';
import { FelaTheme, } from 'react-fela';

import type { Node, } from 'react';

import ExpirationBenchmarkFilter from '../../components/ExpirationBenchmarkFilter/ExpirationBenchmarkFilter';
import PageRow from '../../components/PageRow/PageRow';
import RowItem from '../../components/RowItem/RowItem';
import MainLayout from '../../layouts/MainLayout';

import optionsFiltersQuery from '../queries/optionsFilters.graphql';
import OptionsAssetsQuery from '../queries/optionsInitQuery.graphql';
import useAllAdUnits from '../../components/AdManager/AdUnitsProvider';
import useAdUnitPathTemplate, { createAdSlotProps, } from '../../components/AdManager/useAdUnitPathTemplate';

export type ExperationDateType = {
  id: string,
  value: string,
};

export type OptionType = {
  id: string,
  value: string,
  experationDates: ExperationDateType[],
};

type Props = {
  url: {
    pathname: string,
    query: {
      section: string,
    },
    asPath: string,
  },
};

const numToString: number => string = num => num.toLocaleString('he', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

function options({
  url: {
    query: { section, },
    asPath,
  },
}: Props): Node {
  const adUnits = useAllAdUnits();
const adUnitPathTemplate = useAdUnitPathTemplate();

  return (
    <MainLayout section={section}>
      <SeoData
        title="אופציות - TheMarker Finance"
        description="כל המידע על  אופציות נתוני מסחר, נתונים בזמן אמת, גרפים חדשות ועוד באתר TheMarker Finance"
        path={asPath}
      />
      <FelaTheme>
        {theme => (
          <Fragment>
            <PageRow>
              <RowItem title="אופציות מעו״ף">
                <Query query={OptionsAssetsQuery}>
                  {({ loading, error, data, client, }) => {
                    if (loading) return null;
                    if (error) return null;
                    const assets: Array<OptionType> = data.metadata.optionsAssets;

                    const preChange = (expirationDate, callback) => client
                      .query({
                        query: optionsFiltersQuery,
                        variables: {
                          filters: [ { section: 'experationDates', subSection: expirationDate, }, ],
                          count: 11,
                          sortBy: 'volume',
                          sortOrder: 'desc',
                          offset: 0,
                        },
                        fetchPolicy: 'network-only',
                      })
                      .then(() => {
                        callback();
                      });

                    return (
                      <ExpirationBenchmarkFilter assets={assets} preChange={preChange}>
                        {expirationDate => (
                          <SortableTable
                            miscStyles={{
                              marginTop: [
                                { until: 's', value: '16rem', },
                                { from: 's', value: '7rem', },
                              ],
                              tableLayout: 'auto',
                            }}
                            filters={[ { section: 'experationDates', subSection: expirationDate, }, ]}
                            loadMore
                            type="options"
                            fragment="
                                name
                                value
                                changePercentage
                                volume
                                openPositions
                                nearMoneyRank
                                openPositionsChangeRate
                                assetNumber
                                putCallRatio
                               "
                            fields={[
                              {
                                name: 'name',
                                display: 'שם אופציה',
                                sortingOrder: 'asc',
                                style: () => ({
                                  fontWeight: '700',
                                  maxWidth: '17rem',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                }),
                                value: ({ name, }) => name || '-',
                              },
                              {
                                name: 'value',
                                display: 'שער',
                                sortingOrder: 'desc',
                                value: ({ value, }) => (value ? numToString(value) : '-'),
                              },
                              {
                                name: 'nearMoneyRank',
                                display: 'כסף',
                                sortingOrder: 'desc',
                                style: () => ({ display: 'none', }),
                                headerStyle: { display: 'none', },
                                value: ({ nearMoneyRank, }) => nearMoneyRank || '-',
                              },
                              {
                                name: 'changePercentage',
                                display: '% שינוי',
                                sortingOrder: 'desc',
                                hideUntil: [ 'l', ],
                                style: ({ changePercentage, }) => ({
                                  ...(changePercentage
                                    ? {
                                      color:
                                          changePercentage < 0
                                            ? theme.color('negative')
                                            : theme.color('positive'),
                                    }
                                    : {}),
                                  direction: 'ltr',
                                  fontWeight: '700',
                                  paddingEnd: '2rem',
                                  position: 'relative',
                                  textAlign: 'start',
                                }),
                                value: ({ changePercentage, }) => `
                                  ${changePercentage > 0 ? '' : changePercentage < 0 ? '-' : ''}
                                  ${
                                    changePercentage
                                      ? `${numToString(Math.abs(changePercentage))}%`
                                      : '-'
                                  }
                                `,
                              },
                              {
                                name: 'volume',
                                display: 'מחזור (א׳ ש״ח)',
                                sortingOrder: 'desc',
                                value: ({ volume, }) => (volume ? numToString(volume) : '-'),
                              },
                              {
                                name: 'openPositions',
                                display: 'פוזיציות פתוחות',
                                sortingOrder: 'desc',
                                hideUntil: [ 'l', ],
                                value: ({ openPositions, }) => (openPositions ? numToString(openPositions) : '-'),
                              },
                              {
                                name: 'openPositionsChangeRate',
                                display: 'שינוי\nפוזיציות פתוחות',
                                sortingOrder: 'desc',
                                hideUntil: [ 'l', ],
                                style: ({ openPositionsChangeRate, }) => ({
                                  ...(openPositionsChangeRate
                                    ? {
                                      color:
                                          openPositionsChangeRate < 0
                                            ? theme.color('negative')
                                            : theme.color('positive'),
                                    }
                                    : {}),
                                  direction: 'ltr',
                                  fontWeight: '700',
                                  paddingEnd: '2rem',
                                  position: 'relative',
                                  textAlign: 'start',
                                }),
                                value: ({ openPositionsChangeRate, }) => `
                                  ${
                                    openPositionsChangeRate > 0
                                      ? ''
                                      : openPositionsChangeRate < 0
                                        ? '-'
                                        : ''
                                  }
                                  ${
                                    openPositionsChangeRate
                                      ? `${numToString(Math.abs(openPositionsChangeRate))}%`
                                      : '-'
                                  }
                                `,
                              },
                              {
                                name: 'assetNumber',
                                display: 'מס׳ ני״ע',
                                sortingOrder: 'desc',
                                hideUntil: [ 'l', ],
                                value: ({ assetNumber, }) => assetNumber || '-',
                              },
                              {
                                name: 'putCallRatio',
                                display: 'יחס P/C',
                                sortingOrder: 'desc',
                                hideUntil: [ 'l', ],
                                value: ({ putCallRatio, }) => (putCallRatio ? numToString(putCallRatio) : '-'),
                              },
                            ]}
                            initialSort="nearMoneyRank"
                            initialSortOrder="asc"
                            count={11}
                          />
                        )}
                      </ExpirationBenchmarkFilter>
                    );
                  }}
                </Query>
              </RowItem>
            </PageRow>
            <PageRow>
              <RowItem>
                <AdSlot {...createAdSlotProps(adUnits['finance.themarker.com.banner1'], adUnitPathTemplate)} />
              </RowItem>
            </PageRow>
          </Fragment>
        )}
      </FelaTheme>
    </MainLayout>
  );
}

export default options;
